/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { HardwareStats } from "@/api/monitor";
import { SystemDetail } from "@/api/serverDetail";
import { fmtBytes, fmtDuration } from "./kit";

export type AlertLevel = "crit" | "warn" | "info";

export interface ServerAlert {
  level: AlertLevel;
  title: string;
  detail?: string;
}

const rank: Record<AlertLevel, number> = { crit: 0, warn: 1, info: 2 };

/** Turn a detail snapshot (+ optional hardware stats) into a prioritised problem list. */
export function computeAlerts(d: SystemDetail, hw?: HardwareStats): ServerAlert[] {
  const out: ServerAlert[] = [];
  const add = (level: AlertLevel, title: string, detail?: string) => out.push({ level, title, detail });

  for (const p of d.zfs ?? []) {
    const bad = p.health !== "ONLINE";
    if (bad) {
      add(
        p.health === "DEGRADED" ? "warn" : "crit",
        `ZFS pool ${p.name} is ${p.health}`,
        [p.status, p.action].filter(Boolean).join(" "),
      );
    }
    if (p.errors && p.errors !== "No known data errors") add("crit", `ZFS pool ${p.name} reports data errors`, p.errors);
    if (p.cap_percent >= 95) add("crit", `ZFS pool ${p.name} is ${p.cap_percent}% full`);
    else if (p.cap_percent >= 85) add("warn", `ZFS pool ${p.name} is ${p.cap_percent}% full`);
    for (const v of p.devices ?? []) {
      if (v.depth > 0 && v.state !== "ONLINE" && v.state !== "AVAIL" && v.state !== "DEGRADED") {
        add("warn", `${v.name} is ${v.state}`, `in pool ${p.name}`);
      }
      const errs = [v.read, v.write, v.cksum].some((x) => x !== "0" && x !== "" && !isNaN(Number(x)) && Number(x) > 0);
      if (errs) add("crit", `ZFS errors on ${v.name}`, `read ${v.read} / write ${v.write} / cksum ${v.cksum}`);
    }
  }

  for (const s of d.smart ?? []) {
    const label = s.model ? `${s.model} (${s.device})` : s.device;
    if (s.health === "failed") add("crit", `SMART failed: ${label}`, "The drive reports it is failing; replace it.");
    if ((s.pending ?? 0) > 0) add("warn", `${label}: ${s.pending} pending sectors`, "Unstable sectors waiting to be reallocated.");
    if ((s.reallocated ?? 0) > 0) add("warn", `${label}: ${s.reallocated} reallocated sectors`);
    if ((s.uncorrectable ?? 0) > 0) add("warn", `${label}: ${s.uncorrectable} uncorrectable errors`);
    if ((s.wear_percent_used ?? 0) >= 80) add("warn", `${label}: ${s.wear_percent_used}% of SSD life used`);
    const hot = s.rotational ? 55 : 70;
    if ((s.temp_c ?? 0) >= hot) add("warn", `${label} is hot: ${s.temp_c} °C`);
  }

  for (const u of d.failed_units ?? []) {
    add("warn", `Service failed: ${u.unit}`, u.description);
  }

  if (d.ups) {
    const st = d.ups.status.split(/\s+/);
    if (st.includes("OB")) add("crit", "UPS is running on battery", `${fmtDuration(d.ups.runtime_seconds)} of runtime left`);
    if (st.includes("LB")) add("crit", "UPS battery is low");
    if (st.includes("RB")) add("warn", "UPS battery needs replacement");
    if (st.includes("OVER")) add("crit", "UPS is overloaded");
  }

  const psi = d.pressure;
  if (psi) {
    if (psi.io_full.avg60 >= 20) add("warn", "Heavy I/O pressure", `Tasks stalled on disk ${psi.io_full.avg60.toFixed(0)}% of the last minute.`);
    if (psi.mem_some.avg60 >= 10) add("warn", "Memory pressure", `Tasks waiting for memory ${psi.mem_some.avg60.toFixed(0)}% of the last minute.`);
    if (psi.cpu_some.avg60 >= 50) add("warn", "CPU pressure", `Runnable tasks waiting for a core ${psi.cpu_some.avg60.toFixed(0)}% of the last minute.`);
  }
  if (d.cpu.iowait >= 25) add("warn", `High I/O wait: ${d.cpu.iowait.toFixed(0)}%`);
  if (d.cpu.steal >= 10) add("warn", `CPU steal ${d.cpu.steal.toFixed(0)}%`, "The hypervisor is taking CPU time away from this VM.");
  const threads = d.cpu.threads || (d.cpu.per_core?.length ?? 0) || 1;
  if (d.cpu.load5 > threads * 1.5) add("warn", `Load average ${d.cpu.load5.toFixed(1)} on ${threads} threads`);

  if (hw) {
    if (hw.memory.used_percent >= 92) add("warn", `Memory ${hw.memory.used_percent.toFixed(0)}% used`, `${fmtBytes(hw.memory.available)} available`);
    if (hw.memory.swap_total > 0 && hw.memory.swap_percent >= 50) add("warn", `Swap ${hw.memory.swap_percent.toFixed(0)}% used`);
    for (const fs of hw.disks ?? []) {
      if (fs.used_percent >= 95) add("crit", `${fs.path} is ${fs.used_percent.toFixed(0)}% full`);
      else if (fs.used_percent >= 90) add("warn", `${fs.path} is ${fs.used_percent.toFixed(0)}% full`);
    }
    for (const t of hw.temperature ?? []) {
      const crit = t.critical && t.critical > 0 ? t.critical : 0;
      const name = t.label || t.sensor_key;
      if (crit && t.temperature >= crit - 3) add("crit", `${name} at ${t.temperature.toFixed(0)} °C`, `critical threshold ${crit.toFixed(0)} °C`);
      else if (t.temperature >= (crit ? crit - 15 : 85)) add("warn", `${name} at ${t.temperature.toFixed(0)} °C`);
    }
  }

  for (const n of d.nics ?? []) {
    const errs = n.rx_errors + n.tx_errors;
    if (n.up && errs > 0) add("info", `${n.name}: ${errs} interface errors since boot`);
  }

  for (const c of d.docker ?? []) {
    if (c.state === "restarting") add("crit", `Container ${c.name} is restarting`, c.status);
    else if (c.state === "exited" && /Exited \((?!0\))/.test(c.status)) add("warn", `Container ${c.name} exited with an error`, c.status);
  }

  return out.sort((a, b) => rank[a.level] - rank[b.level]);
}
